import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { SERVER_URL } from '@/shared/lib/serverUrl'
import {
  readStorage,
  removeStorage,
  VOUCHER_CODE_KEY,
  VOUCHER_DISCOUNT_KEY,
  writeStorage,
} from '@/shared/lib/storageKeys'

/**
 * Der Gutschein einer Hebamme. Code und Rabatt liegen im `localStorage`, weil die
 * Zahlung über Stripe die Seite verlässt und zurückkommt - ohne das wäre der Rabatt
 * unterwegs verloren.
 *
 * Der Code kommt aus drei Richtungen: aus dem Speicher, aus `?ref=CODE` in einem
 * Empfehlungslink, oder von Hand eingetippt. Geprüft wird er immer beim Server, denn
 * nur dort steht, ob er noch gilt.
 */
export function useVoucher() {
  const [searchParams] = useSearchParams()

  const [code, setCode] = useState(() => readStorage(VOUCHER_CODE_KEY) || '')
  const [discount, setDiscount] = useState<number | null>(() => {
    const saved = readStorage(VOUCHER_DISCOUNT_KEY)
    return saved ? Number(saved) : null
  })
  const [error, setError] = useState<string | null>(null)
  const [isValidating, setIsValidating] = useState(false)

  // Empfehlungslink einer Hebamme: ?ref=CODE hinterlegt den Gutschein.
  useEffect(() => {
    const refFromUrl = searchParams.get('ref')
    if (!refFromUrl) return
    writeStorage(VOUCHER_CODE_KEY, refFromUrl.toUpperCase())
    setCode(refFromUrl.toUpperCase())
  }, [searchParams])

  /** Welcher Code schon geprüft wurde - sonst liefe die Prüfung in einer Schleife. */
  const validationAttempted = useRef<string | null>(null)

  /**
   * `silent` ist die Prüfung des hinterlegten Codes beim Öffnen: Sie darf nichts
   * verwerfen und nichts anzeigen, denn die Nutzerin hat gerade nichts eingegeben.
   */
  const validate = async (candidate: string, silent = false) => {
    if (!candidate.trim()) {
      if (!silent) setError('Bitte gib einen Code ein')
      return
    }

    setIsValidating(true)
    setError(null)

    try {
      const res = await fetch(`${SERVER_URL}/api/validate-voucher-code/${candidate.toUpperCase()}`)
      const data = await res.json()

      if (data.valid) {
        setDiscount(data.discount_percent)
        setCode(candidate.toUpperCase())
        writeStorage(VOUCHER_CODE_KEY, candidate.toUpperCase())
        writeStorage(VOUCHER_DISCOUNT_KEY, String(data.discount_percent))
        return true
      }

      if (!silent) {
        setError(data.error || 'Ungültiger Code')
        removeStorage(VOUCHER_CODE_KEY)
        removeStorage(VOUCHER_DISCOUNT_KEY)
        setCode('')
        setDiscount(null)
      }
      return false
    } catch (caught) {
      console.error('Voucher validation error:', caught)
      if (!silent) setError('Fehler bei der Validierung')
      // Nach einem Netzfehler darf es der nächste Durchgang nochmal versuchen.
      validationAttempted.current = null
      return false
    } finally {
      setIsValidating(false)
    }
  }

  // Hinterlegten Code einmal selbst prüfen, damit der Rabatt im Preis steht.
  const codeToValidate = code && !discount && !isValidating ? code : null
  useEffect(() => {
    if (!codeToValidate) return
    if (validationAttempted.current === codeToValidate) return
    validationAttempted.current = codeToValidate
    validate(codeToValidate, true)
  }, [codeToValidate])

  return { code, setCode, discount, error, isValidating, validate }
}
